import { Autocomplete, TextField, AutocompleteProps } from '@mui/material';
import { FC } from 'react';
import { Controller } from 'react-hook-form';
import FieldGridContainer from '../fieldGridContainer';
import { TKeyValue } from '../../../utils/commonInterfaces';

export type ControlledTypeaheadProps = Omit<
  AutocompleteProps<TKeyValue<string>, false, false, false>,
  'renderInput' | 'options' | 'onChange' | 'value'
> & {
  control?: any;
  isGridItem?: boolean;
  label: string;
  id: string;
  name: string;
  errorMessage?: string;
  isFullWidth?: boolean;
  dataTestId?: string;
  options: TKeyValue<string>[];
  isRequired?: boolean;
  helperText?: string;
};

const ControlledTypeahead: FC<ControlledTypeaheadProps> = (
  props: ControlledTypeaheadProps
) => {
  const {
    control,
    isGridItem = true,
    label,
    id,
    name,
    helperText = '',
    errorMessage,
    isFullWidth = true,
    dataTestId = 'typeahead-container',
    options = [],
    isRequired = false,
    sx,
    ...otherProps
  } = props;

  const getTypeahead = () => {
    return (
      <Controller
        name={name ?? ''}
        control={control}
        render={({ field }) => {
          const { ref, value, onChange, onBlur, ...fieldProps } = field;
          
          // Find the selected option based on the value (which is the key/id)
          const selectedOption = options.find((option) => option.key === value) || null;

          return (
            <Autocomplete
              {...fieldProps}
              {...otherProps}
              id={id}
              options={options}
              value={selectedOption}
              onChange={(_, newValue) => {
                // Store the key (ID) as the form value
                onChange(newValue ? newValue.key : '');
              }}
              onBlur={onBlur}
              getOptionLabel={(option) => option.value || ''}
              isOptionEqualToValue={(option, val) => option.key === val.key}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={ref}
                  label={label}
                  size="small"
                  fullWidth
                  required={isRequired}
                  helperText={errorMessage ?? helperText}
                  error={!!errorMessage}
                  data-testid={dataTestId || `typeahead-${id}`}
                  sx={{
                    '& .MuiInputBase-root': {
                      minHeight: {
                        xs: '44px',
                        sm: '40px',
                      },
                      fontSize: {
                        xs: '16px', // Prevents zoom on iOS
                        sm: '14px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                      },
                    },
                    ...sx,
                  }}
                />
              )}
              sx={{
                width: '100%',
                '& .MuiAutocomplete-inputRoot': {
                  fontSize: {
                    xs: '16px',
                    sm: '14px',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: {
                    xs: '0.875rem',
                    sm: '1rem',
                  },
                },
                ...sx,
              }}
            />
          );
        }}
      />
    );
  };

  if (isGridItem) {
    return (
      <FieldGridContainer
        isFullWidth={isFullWidth}
        dataTestId={dataTestId || `typeahead-container-${id}`}
      >
        {getTypeahead()}
      </FieldGridContainer>
    );
  }

  return (
    <div data-testid={dataTestId || `typeahead-container-${id}`}>
      {getTypeahead()}
    </div>
  );
};

export default ControlledTypeahead;
